import { supabase } from '../utils/supabase';

/**
 * Review Service
 * Handles all review and rating operations with Supabase
 */
class ReviewService {
  /**
   * Get reviews for a vendor
   * @param {string} vendorId - Vendor UUID
   * @param {Object} filters - Filter options
   * @returns {Promise<Object>} Result with reviews data
   */
  async getVendorReviews(vendorId, filters = {}) {
    try {
      let query = supabase
        .from('reviews')
        .select(`
          *,
          user_profiles!user_id (
            id,
            full_name,
            profile_image
          )
        `, { count: 'exact' })
        .eq('vendor_id', vendorId);

      // Apply filters
      if (filters.minRating) {
        query = query.gte('rating', filters.minRating);
      }

      if (filters.isVerified !== undefined) {
        query = query.eq('is_verified', filters.isVerified);
      }

      // Apply sorting
      const sortBy = filters.sortBy || 'created_at';
      const sortOrder = filters.sortOrder || 'desc';
      query = query.order(sortBy, { ascending: sortOrder === 'asc' });

      // Apply pagination
      if (filters.limit) {
        query = query.limit(filters.limit);
      }

      const { data, error, count } = await query;

      if (error) {
        console.error('Error fetching vendor reviews:', error);
        return { success: false, error: error.message };
      }

      // Calculate average rating
      const averageRating = data && data.length > 0
        ? (data.reduce((sum, review) => sum + review.rating, 0) / data.length).toFixed(1)
        : 0;

      return {
        success: true,
        data: data || [],
        count,
        averageRating: parseFloat(averageRating)
      };
    } catch (error) {
      console.error('Error in getVendorReviews:', error);
      if (error?.message?.includes('Failed to fetch')) {
        return {
          success: false,
          error: 'Cannot connect to database. Please check your connection.'
        };
      }
      return { success: false, error: 'Failed to fetch reviews' };
    }
  }

  /**
   * Get a single review by ID
   * @param {string} reviewId - Review UUID
   * @returns {Promise<Object>} Result with review data
   */
  async getReview(reviewId) {
    try {
      const { data, error } = await supabase
        .from('reviews')
        .select(`
          *,
          user_profiles!user_id (
            id,
            full_name,
            profile_image
          ),
          vendor_profiles!vendor_id (
            id,
            business_name
          )
        `)
        .eq('id', reviewId)
        .single();

      if (error) {
        console.error('Error fetching review:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error in getReview:', error);
      return { success: false, error: 'Failed to fetch review' };
    }
  }

  /**
   * Get reviews by user
   * @param {string} userId - User UUID
   * @returns {Promise<Object>} Result with user's reviews
   */
  async getUserReviews(userId) {
    try {
      const { data, error } = await supabase
        .from('reviews')
        .select(`
          *,
          vendor_profiles!vendor_id (
            id,
            business_name,
            category,
            city,
            state
          )
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching user reviews:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data: data || [] };
    } catch (error) {
      console.error('Error in getUserReviews:', error);
      return { success: false, error: 'Failed to fetch user reviews' };
    }
  }

  /**
   * Submit a new review
   * @param {Object} reviewData - Review data
   * @returns {Promise<Object>} Result with created review
   */
  async submitReview(reviewData) {
    try {
      // Validate required fields
      if (!reviewData.vendor_id || !reviewData.user_id || !reviewData.rating) {
        return {
          success: false,
          error: 'Vendor ID, User ID, and rating are required'
        };
      }

      // Validate rating is between 1 and 5
      if (reviewData.rating < 1 || reviewData.rating > 5) {
        return {
          success: false,
          error: 'Rating must be between 1 and 5'
        };
      }

      // Check if user has already reviewed this vendor
      const { data: existingReview } = await supabase
        .from('reviews')
        .select('id')
        .eq('vendor_id', reviewData.vendor_id)
        .eq('user_id', reviewData.user_id)
        .single();

      if (existingReview) {
        return {
          success: false,
          error: 'You have already reviewed this vendor. Please update your existing review instead.'
        };
      }

      const { data, error } = await supabase
        .from('reviews')
        .insert({
          ...reviewData,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select(`
          *,
          user_profiles!user_id (
            id,
            full_name,
            profile_image
          )
        `)
        .single();

      if (error) {
        console.error('Error submitting review:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error in submitReview:', error);
      return { success: false, error: 'Failed to submit review' };
    }
  }

  /**
   * Update an existing review
   * @param {string} reviewId - Review UUID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} Result with updated review
   */
  async updateReview(reviewId, updates) {
    try {
      // Validate rating if provided
      if (updates.rating && (updates.rating < 1 || updates.rating > 5)) {
        return {
          success: false,
          error: 'Rating must be between 1 and 5'
        };
      }

      const { data, error } = await supabase
        .from('reviews')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', reviewId)
        .select(`
          *,
          user_profiles!user_id (
            id,
            full_name,
            profile_image
          )
        `)
        .single();

      if (error) {
        console.error('Error updating review:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error in updateReview:', error);
      return { success: false, error: 'Failed to update review' };
    }
  }

  /**
   * Delete a review
   * @param {string} reviewId - Review UUID
   * @param {string} userId - User UUID (for authorization)
   * @returns {Promise<Object>} Result of deletion
   */
  async deleteReview(reviewId, userId) {
    try {
      // First verify the review belongs to the user
      const { data: review, error: fetchError } = await supabase
        .from('reviews')
        .select('user_id')
        .eq('id', reviewId)
        .single();

      if (fetchError) {
        return { success: false, error: 'Review not found' };
      }

      if (review.user_id !== userId) {
        return { success: false, error: 'You can only delete your own reviews' };
      }

      const { error } = await supabase
        .from('reviews')
        .delete()
        .eq('id', reviewId);

      if (error) {
        console.error('Error deleting review:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      console.error('Error in deleteReview:', error);
      return { success: false, error: 'Failed to delete review' };
    }
  }

  /**
   * Mark review as helpful
   * @param {string} reviewId - Review UUID
   * @returns {Promise<Object>} Result with updated review
   */
  async markReviewHelpful(reviewId) {
    try {
      // Increment helpful_count
      const { data, error } = await supabase
        .rpc('increment_review_helpful_count', { review_id: reviewId });

      if (error) {
        // If function doesn't exist, do it manually
        const { data: review, error: fetchError } = await supabase
          .from('reviews')
          .select('helpful_count')
          .eq('id', reviewId)
          .single();

        if (fetchError) {
          return { success: false, error: 'Review not found' };
        }

        const { data: updated, error: updateError } = await supabase
          .from('reviews')
          .update({ helpful_count: (review.helpful_count || 0) + 1 })
          .eq('id', reviewId)
          .select()
          .single();

        if (updateError) {
          return { success: false, error: updateError.message };
        }

        return { success: true, data: updated };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error in markReviewHelpful:', error);
      return { success: false, error: 'Failed to mark review as helpful' };
    }
  }

  /**
   * Get review statistics for a vendor
   * @param {string} vendorId - Vendor UUID
   * @returns {Promise<Object>} Result with review statistics
   */
  async getReviewStats(vendorId) {
    try {
      const { data, error } = await supabase
        .from('reviews')
        .select('rating, is_verified')
        .eq('vendor_id', vendorId);

      if (error) {
        console.error('Error fetching review stats:', error);
        return { success: false, error: error.message };
      }

      const reviews = data || [];
      const totalReviews = reviews.length;

      if (totalReviews === 0) {
        return {
          success: true,
          data: {
            totalReviews: 0,
            averageRating: 0,
            verifiedReviews: 0,
            ratingDistribution: {
              5: 0,
              4: 0,
              3: 0,
              2: 0,
              1: 0
            }
          }
        };
      }

      // Calculate statistics
      const totalRating = reviews.reduce((sum, review) => sum + review.rating, 0);
      const averageRating = (totalRating / totalReviews).toFixed(1);
      const verifiedReviews = reviews.filter(r => r.is_verified).length;

      // Calculate rating distribution
      const ratingDistribution = {
        5: reviews.filter(r => r.rating === 5).length,
        4: reviews.filter(r => r.rating === 4).length,
        3: reviews.filter(r => r.rating === 3).length,
        2: reviews.filter(r => r.rating === 2).length,
        1: reviews.filter(r => r.rating === 1).length
      };

      return {
        success: true,
        data: {
          totalReviews,
          averageRating: parseFloat(averageRating),
          verifiedReviews,
          ratingDistribution,
          percentageByRating: {
            5: ((ratingDistribution[5] / totalReviews) * 100).toFixed(1),
            4: ((ratingDistribution[4] / totalReviews) * 100).toFixed(1),
            3: ((ratingDistribution[3] / totalReviews) * 100).toFixed(1),
            2: ((ratingDistribution[2] / totalReviews) * 100).toFixed(1),
            1: ((ratingDistribution[1] / totalReviews) * 100).toFixed(1)
          }
        }
      };
    } catch (error) {
      console.error('Error in getReviewStats:', error);
      return { success: false, error: 'Failed to fetch review statistics' };
    }
  }

  /**
   * Check if user can review a vendor (has completed booking)
   * @param {string} userId - User UUID
   * @param {string} vendorId - Vendor UUID
   * @returns {Promise<Object>} Result with eligibility status
   */
  async canUserReview(userId, vendorId) {
    try {
      // Check if user has a completed booking with this vendor
      const { data: bookings, error: bookingError } = await supabase
        .from('bookings')
        .select('id, status')
        .eq('user_id', userId)
        .eq('vendor_id', vendorId)
        .eq('status', 'completed');

      if (bookingError) {
        return { success: false, error: bookingError.message };
      }

      if (!bookings || bookings.length === 0) {
        return {
          success: true,
          canReview: false,
          reason: 'You must complete a booking with this vendor before leaving a review'
        };
      }

      // Check if user has already reviewed this vendor
      const { data: existingReview } = await supabase
        .from('reviews')
        .select('id')
        .eq('user_id', userId)
        .eq('vendor_id', vendorId)
        .single();

      if (existingReview) {
        return {
          success: true,
          canReview: false,
          reason: 'You have already reviewed this vendor',
          hasReviewed: true
        };
      }

      return {
        success: true,
        canReview: true,
        hasCompletedBooking: true
      };
    } catch (error) {
      console.error('Error in canUserReview:', error);
      return { success: false, error: 'Failed to check review eligibility' };
    }
  }

  /**
   * Subscribe to real-time review updates for a vendor
   * @param {string} vendorId - Vendor UUID
   * @param {Function} callback - Callback function for updates
   * @returns {Object} Subscription object
   */
  subscribeToReviewUpdates(vendorId, callback) {
    const subscription = supabase
      .channel(`review_updates_${vendorId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'reviews',
          filter: `vendor_id=eq.${vendorId}`
        },
        callback
      )
      .subscribe();

    return subscription;
  }

  /**
   * Unsubscribe from real-time updates
   * @param {Object} subscription - Subscription object
   */
  unsubscribeFromUpdates(subscription) {
    if (subscription) {
      supabase.removeChannel(subscription);
    }
  }
}

// Export singleton instance
const reviewService = new ReviewService();
export default reviewService;
